import * as React from 'react';
import { observer } from 'mobx-react-lite';
import WrapperSynchronizer from '../../services/WrapperSynchronizer';
import PageElementWrapper from '../../wrappers/PageElementWrapper';
import { IElement } from '../../model/EditorStateMachine';
import IframeServiceInjector from '../../services/injectors/IframeServiceInjector';
import EditorStateMachine from '../../model/EditorStateMachine';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { ELEMENT_TYPE_ARTICLE, ELEMENT_TYPE_PICTURE } from '../../consts/element-types';
import { useTranslation } from 'react-i18next';

interface FCProps {
  elementData: IElement;
  iframeElement: HTMLIFrameElement;
  elementId: number;
  hoverButton?: React.ReactNode;
  hoverButtonPosition?: 'center' | 'top-right';
  showLoader?: boolean;
}

const ElementWrapper: React.FC<FCProps> = observer(
  ({ elementData, iframeElement, elementId, hoverButton, showLoader, hoverButtonPosition }) => {
    const iframeRect = iframeElement.getBoundingClientRect();

    // get top position relative to the iframe element
    let top = elementData.elementPosition.top + iframeRect.top;
    let left = elementData.elementPosition.left + iframeRect.left;
    let height = elementData.elementPosition.height;
    let width = elementData.elementPosition.width;

    // Clip top
    if (top < iframeRect.top) {
      height = height - (iframeRect.top - top);
      top = iframeRect.top;
    }

    // // Clip bottom
    // const bottom = top + height;
    // const iframeBottom = iframeRect.top + iframeRect.height;
    // if (bottom > iframeBottom) {
    //   height = height - (bottom - iframeBottom);
    // }

    // Check if this element is currently hovered
    const isHovered = EditorStateMachine.hoveredElementId === elementId;
    
    // Check if we are in edit mode for the section
    const isInEditMode = EditorStateMachine.isSectionInEditMode;

    // Check if element is completely outside iframe bounds
    const isCompletelyHidden = height <= 0 || width <= 0;

    const styles: React.CSSProperties = {
      top: top + 'px',
      left: left + 'px',
      width: width + 'px',
      height: height + 'px',
      display: isCompletelyHidden ? 'none' : 'block',
    };

    // In edit mode: always show outline, show background only on hover
    if (isInEditMode) {
      styles.outline = '2px solid #8AD1F1';
      styles.outlineOffset = '2px';
      styles.backgroundColor = isHovered ? 'rgba(238, 251, 255, 0.50)' : 'transparent';
      styles.transition = 'background-color 0.2s';
      styles.cursor = 'pointer';
    }

    if (showLoader) {
      styles.backgroundColor = 'rgba(255, 255, 255, 0.7)';
    }

    const { t } = useTranslation();

    // Determine the label text based on element type (i18n)
    const getElementLabel = () => {
      if (elementData.elementType === ELEMENT_TYPE_ARTICLE) {
        return 'Text';
      }
      if (elementData.elementType === ELEMENT_TYPE_PICTURE) {
        return t('Image');
      }
      return null;
    };

    const elementLabel = getElementLabel();

    return (
      <div
        ref={(ref) => {
          const WrapperSynchronizer =
            IframeServiceInjector.getService<WrapperSynchronizer>(
              'WrapperSynchronizer'
            );

          if (ref === null) {
            WrapperSynchronizer.unregisterElement(elementId);
            return;
          }

          WrapperSynchronizer.registerElement(
            {
              element: new PageElementWrapper(ref, iframeElement)
            },
            elementId
          );
        }}
        className="fixed pointer-events-none"
        style={styles}>
        {/* Element type label in top right corner when in edit mode */}
        {isInEditMode && elementLabel && (
          <div className="absolute top-[-2px] right-[-2px] bg-[#8AD1F1] text-white px-2 py-1">
            {elementLabel}
          </div>
        )}
        {/* Show buttons only when hovered in edit mode. Optionally render a full-size clickable overlay. */}
        {isInEditMode && isHovered && hoverButton && (
          (hoverButtonPosition === 'top-right') ? (
            <div
              onMouseMove={(e) => e.nativeEvent.stopImmediatePropagation()}
              className="pointer-events-none absolute top-[70px] right-[18px]"
              style={{ zIndex: 2 }}>
              <div className="pointer-events-none m-2">{hoverButton}</div>
            </div>
          ) : (
            <div
              onMouseMove={(e) => e.nativeEvent.stopImmediatePropagation()}
              className="pointer-events-none absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
              style={{ zIndex: 2 }}>
              {hoverButton}
            </div>
          )
        )}
        {showLoader && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}>
            <div
              style={{
                display: 'flex',
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: '50px', color: 'black' }}
                  />
                }
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default ElementWrapper;
